import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { TOUR_IDS } from './onboardingConfig.js';
import { mergeTourProgress, isTourDone } from './onboardingStorage.js';
import { STEP_CONTEXTUAL_TOUR } from './onboardingNavigation.js';
import { useOnboarding } from './useOnboarding.js';
import { OnboardingContext } from './onboardingContext.js';
import { suggestNextTour, getAssistantPayload } from './onboardingAssistant.js';

/**
 * @param {{
 *   children: React.ReactNode,
 *   currentStep: string,
 *   setCurrentStep: (step: string) => void,
 *   setCurrentProperty?: (p: object) => void,
 *   currentProperty?: { id?: string } | null,
 *   currentScenario?: { id?: string } | null,
 *   disabled?: boolean,
 * }} props
 */
export function OnboardingProvider({
  children,
  currentStep,
  setCurrentStep,
  setCurrentProperty,
  currentProperty = null,
  currentScenario = null,
  disabled = false,
}) {
  const {
    currentUser,
    userProfile,
    profileLoading,
    impersonation,
    properties,
    updateUserProfile,
  } = useAuth();
  const { t, language } = useLanguage();

  const uid = currentUser?.uid;
  const persona = userProfile?.persona;
  const hasProperties = (properties?.length ?? 0) > 0;

  const [tourProgress, setTourProgress] = useState(() =>
    uid ? mergeTourProgress(uid, userProfile?.tourProgress) : {},
  );

  useEffect(() => {
    if (!uid) {
      setTourProgress({});
      return;
    }
    setTourProgress(mergeTourProgress(uid, userProfile?.tourProgress));
  }, [uid, userProfile?.tourProgress]);

  const onboarding = useOnboarding({
    uid,
    persona,
    t,
    currentStep,
    hasProperties,
    currentProperty,
    currentScenario,
    properties,
    setCurrentStep,
    setCurrentProperty,
    tourProgress,
    setTourProgress,
    updateUserProfile,
  });

  const {
    startTour,
    isRunning,
    destroyDriver,
    canStartTour,
  } = onboarding;

  const welcomeAutoStartedRef = useRef(false);
  const contextualAutoStartedRef = useRef(new Set());

  const personaModalVisible =
    Boolean(currentUser) &&
    !profileLoading &&
    (!userProfile || userProfile.onboardingCompleted !== true);

  const canAutoStartBase =
    !disabled &&
    !personaModalVisible &&
    !impersonation?.active &&
    Boolean(uid) &&
    userProfile?.onboardingCompleted === true;

  const welcomeDone = isTourDone(tourProgress, TOUR_IDS.WELCOME);

  useEffect(() => {
    if (!canAutoStartBase || welcomeDone || welcomeAutoStartedRef.current || isRunning) {
      return undefined;
    }
    if (currentStep !== 'home') return undefined;

    const timer = window.setTimeout(() => {
      welcomeAutoStartedRef.current = true;
      startTour(TOUR_IDS.WELCOME);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [canAutoStartBase, welcomeDone, isRunning, startTour, currentStep]);

  useEffect(() => {
    if (!canAutoStartBase || isRunning) return undefined;

    const contextualTourId = STEP_CONTEXTUAL_TOUR[currentStep];
    if (!contextualTourId) return undefined;
    if (isTourDone(tourProgress, contextualTourId)) return undefined;
    if (contextualAutoStartedRef.current.has(contextualTourId)) return undefined;
    if (!canStartTour(contextualTourId)) return undefined;

    const timer = window.setTimeout(() => {
      contextualAutoStartedRef.current.add(contextualTourId);
      startTour(contextualTourId);
    }, 600);

    return () => window.clearTimeout(timer);
  }, [
    canAutoStartBase,
    currentStep,
    isRunning,
    startTour,
    tourProgress,
    canStartTour,
  ]);

  useEffect(() => {
    return () => {
      destroyDriver();
    };
  }, [destroyDriver]);

  const tourContext = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      persona,
      activePropertyId: currentProperty?.id ?? null,
      activeScenarioId: currentScenario?.id ?? null,
      tourProgress,
      locale: language,
      hasProperties,
    }),
    [
      currentStep,
      setCurrentStep,
      persona,
      currentProperty?.id,
      currentScenario?.id,
      tourProgress,
      language,
      hasProperties,
    ],
  );

  const value = useMemo(
    () => ({
      ...onboarding,
      tourContext,
      personaModalVisible,
      impersonationActive: Boolean(impersonation?.active),
      disabled,
      suggestNextTour: () => suggestNextTour(tourContext),
      getAssistantPayload: () => getAssistantPayload(tourContext, t),
    }),
    [onboarding, tourContext, personaModalVisible, impersonation?.active, disabled, t],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export default OnboardingProvider;
